/**
 * QuickActions - горизонтальный скролл как mobile
 * Кнопка добавления, Voice, Receipt, частые категории с иконками в кружках
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { getCategoryIconConfig } from '../../utils/categoryIcons';
import { getAllCategoryIcons, categoryIconsEventEmitter, CATEGORY_ICONS_CHANGED_EVENT } from '../../utils/categoryIconStorage';
import { hapticFeedback } from '../../utils/telegram';
import './QuickActions.css';

export interface FrequentTitle {
  title: string;
  category: string;
  count: number;
}

export interface FrequentCategory {
  category: string;
  icon: string;
  count: number;
  avg_amount: number;
  frequent_titles: string[];
}

interface QuickActionsProps {
  /** Новый интерфейс (mobile-like): частые названия транзакций */
  frequentTitles?: FrequentTitle[];
  onTitleClick?: (item: FrequentTitle) => void;
  onQuickAdd?: (type: 'income' | 'expense') => void;
  onVoicePress?: () => void;
  onReceiptPress?: () => void;
  isPremium?: boolean;
  isLoading?: boolean;
  /** Обратная совместимость: частые категории */
  categories?: FrequentCategory[];
  onCategoryClick?: (category: FrequentCategory) => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({
  frequentTitles = [],
  onTitleClick,
  onQuickAdd,
  onVoicePress,
  onReceiptPress,
  isPremium = false,
  isLoading = false,
  categories = [],
  onCategoryClick,
}) => {
  const { t } = useTranslation();
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadCategoryIcons = () => {
      try {
        setCategoryIcons(getAllCategoryIcons());
      } catch {
        setCategoryIcons({});
      }
    };
    loadCategoryIcons();
    const handleIconsChanged = () => setCategoryIcons(getAllCategoryIcons());
    categoryIconsEventEmitter.on(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    return () => categoryIconsEventEmitter.off(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
  }, []);

  // Объединяем frequentTitles и categories (конвертируем categories в FrequentTitle)
  const items: FrequentTitle[] = frequentTitles.length > 0
    ? frequentTitles.filter((item) => item.title?.trim() && item.category !== 'Цели')
    : categories.map((cat) => ({
        title: cat.frequent_titles?.[0] || cat.category,
        category: cat.category,
        count: cat.count,
      }));

  const handleItemClick = (item: FrequentTitle) => {
    hapticFeedback.selection();
    if (frequentTitles.length > 0 && onTitleClick) {
      onTitleClick(item);
    } else if (categories.length > 0 && onCategoryClick) {
      const cat = categories.find((c) => c.category === item.category);
      if (cat) onCategoryClick(cat);
    }
  };

  const hasActions =
    onQuickAdd ||
    onVoicePress ||
    onReceiptPress ||
    items.length > 0;

  if (!hasActions) {
    return null;
  }

  return (
    <div className="quick-actions-mobile">
      <div className="quick-actions-mobile__scroll">
        {onQuickAdd && (
          <button
            type="button"
            className="quick-actions-mobile__item"
            onClick={() => {
              hapticFeedback.selection();
              onQuickAdd('expense');
            }}
            aria-label="Add transaction"
          >
            <div className="quick-actions-mobile__icon quick-actions-mobile__icon--primary">
              <Icon name="add" size={26} color="var(--color-primary)" />
            </div>
            <span className="quick-actions-mobile__label">{t('common.add')}</span>
          </button>
        )}

        {onVoicePress && (
          <button
            type="button"
            className="quick-actions-mobile__item"
            onClick={() => {
              hapticFeedback.selection();
              onVoicePress();
            }}
            aria-label="Voice input"
          >
            <div className="quick-actions-mobile__icon-wrapper">
              <div className="quick-actions-mobile__icon quick-actions-mobile__icon--voice">
                <Icon name="mic" size={26} color="#EF4444" />
              </div>
              {!isPremium && (
                <div className="quick-actions-mobile__lock" style={{ backgroundColor: '#EF4444' }}>
                  <Icon name="lock" size={8} color="#FFFFFF" />
                </div>
              )}
            </div>
            <span className="quick-actions-mobile__label">Voice</span>
          </button>
        )}

        {onReceiptPress && (
          <button
            type="button"
            className="quick-actions-mobile__item"
            onClick={() => {
              hapticFeedback.selection();
              onReceiptPress();
            }}
            aria-label="Scan receipt"
          >
            <div className="quick-actions-mobile__icon-wrapper">
              <div className="quick-actions-mobile__icon quick-actions-mobile__icon--receipt">
                <Icon name="camera" size={26} color="#F59E0B" />
              </div>
              {!isPremium && (
                <div className="quick-actions-mobile__lock" style={{ backgroundColor: '#F59E0B' }}>
                  <Icon name="lock" size={8} color="#FFFFFF" />
                </div>
              )}
            </div>
            <span className="quick-actions-mobile__label">{t('home.scanReceiptTitle')}</span>
          </button>
        )}

        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="quick-actions-mobile__item">
              <div className="quick-actions-mobile__icon quick-actions-mobile__icon--skeleton" />
              <div className="quick-actions-mobile__label quick-actions-mobile__label--skeleton" />
            </div>
          ))
        ) : (
          items.slice(0, 10).map((item, index) => {
            const iconConfig = getCategoryIconConfig(
              item.category || 'Разное',
              categoryIcons[item.category]
            );
            return (
              <button
                key={`${item.title}-${index}`}
                type="button"
                className="quick-actions-mobile__item"
                onClick={() => handleItemClick(item)}
              >
                <div
                  className="quick-actions-mobile__icon"
                  style={{
                    backgroundColor: iconConfig.backgroundColor,
                    color: iconConfig.color,
                  }}
                >
                  <Icon name={iconConfig.icon} size={26} color={iconConfig.color} />
                </div>
                <span className="quick-actions-mobile__label" title={item.title}>
                  {item.title}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default QuickActions;
